import * as yup from 'yup';
import {
  combineRegex,
} from '@americanexpress/axp-field-validations-common';
import {
  alphaLower,
  alphaUpper,
  hyphen,
  numbers,
} from '../regular-expressions';
import {
  getCurrentMonthYear
} from '../utils'

export const allowedPlasticColors = ['PLATINUM', 'GOLD', 'GREEN', 'BLUE'];

export const createMonthYearSchema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .required(`${errorMessagePrefix}required.error`)
    .test('is-valid-length', `${errorMessagePrefix}length.error`, (value) => (value.replace(/\./g, '').length === 6))
    .matches(/^\d{2}\.\d{4}/, `${errorMessagePrefix}format.error`)
    .test('is-month-valid', `${errorMessagePrefix}isValidMonth.error`, (values) => {
      const [month,] = values.split('.');
      return Number.parseInt(month, 10) < 13 && Number.parseInt(month, 10) > 0;
    })
    .test('is-year-valid', `${errorMessagePrefix}isValidYear.error`, (values) => {
      const [, year] = values.split('.');
      const [, currentYear] = getCurrentMonthYear();
      return Number.parseInt(year, 10) > currentYear - 125 && Number.parseInt(year, 10) <= currentYear;
    })
    .test('is-month-year-valid', `${errorMessagePrefix}isValidMonthYear.error`, (values) => {
      const [month, year] = values.split('.');
      const [currentMonth, currentYear] = getCurrentMonthYear();
      const monthValue = Number.parseInt(month, 10);
      const yearValue = Number.parseInt(year, 10);

      return !(yearValue === currentYear && monthValue > currentMonth);
    });
}

export const iban = yup.string()
  .required('iban.required.error')
  .length(27, 'iban.length.error')
  .matches(combineRegex([numbers, alphaLower, alphaUpper, hyphen]), 'iban.regex.error');

export const bic = yup.string()
  .required('bic.required.error')
  .length(11, 'bic.length.error')
  .matches(combineRegex([alphaLower, alphaUpper]), 'bic.regex.error');

export const yearOfCreation = createMonthYearSchema({ errorMessagePrefix: 'yearOfCreation.' });

export const directDebitConsent = yup.boolean()
  .required('directDebitConsent.required.error')
  .isTrue('directDebitConsent.required.error');

export const pin = yup.string()
  .required('pin.required.error')
  .length(4, 'pin.length.error')
  .matches(combineRegex([numbers]), 'pin.regex.error');

export const confirmPin = yup.string()
  .required('confirmPin.required.error')
  .oneOf([yup.ref('pin')], 'confirmPin.regex.error');

export const employmentStatusValues = [
  'EMPLOYED_FULLTIME',
  'EMPLOYED_PART_TIME',
  'BUSINESS_OWNER',
  'SELF_EMPLOYED_OR_FUNDED',
  'RETIRED',
  'STUDENT',
  'HOME_DUTIES',
  'UNEMPLOYED',
  'INDEPENDENT_INCOME',
];

export const suppEmploymentStatusValuesRequired = [
  'EMPLOYED_FULLTIME',
  'EMPLOYED_PART_TIME',
  'BUSINESS_OWNER',
  'SELF_EMPLOYED_OR_FUNDED',
];

export const employmentStatus = yup.string()
  .required('employmentStatus.required.error')
  .oneOf(employmentStatusValues, 'employmentStatus.enum.error');

export const timeAtCurrentJob = createMonthYearSchema({ errorMessagePrefix: 'timeAtCurrentJob.' });

export const industryDependentValues = [
  'EMPLOYED_FULLTIME',
  'EMPLOYED_PART_TIME',
  'BUSINESS_OWNER',
  'SELF_EMPLOYED_OR_FUNDED',
];

export const industryConditionals = {
  required: yup.string().required('industry.required.error'),
  notRequired: yup.string().notRequired(),
};

export const industry = yup.string()
  .when('employmentStatus', ([employmentStatus]) => {
    if (industryDependentValues.includes(employmentStatus)) {
      return industryConditionals.required;
    }

    return industryConditionals.notRequired;
  });

// NOTE: used for backend validations
export const occupationDependentValues = [
  'EMPLOYED_FULLTIME',
  'EMPLOYED_PART_TIME',
  'BUSINESS_OWNER',
  'SELF_EMPLOYED_OR_FUNDED',
];

export const occupationConditionals = {
  required: yup.string().required('occupation.required.error'),
  notRequired: yup.string().notRequired(),
};

export const occupation = yup.string()
  .when('industry', ([industry]) => {
    if (industry) {
      return occupationConditionals.required;
    }

    return occupationConditionals.notRequired;
  });

export const monthlyGrossIncome = yup.string()
  .required('monthlyGrossIncome.required.error');

export const sourceOfIncomeValues = [
  'REGULAR_TAXED_INCOME',
  'RENTAL_INCOME',
  'INVESTMENT_INCOME',
  'RETIREMENT',
  'FINANCIALLY_INDEPENDENT',
  'PUBLIC_ASSISTANCE',
];

export const primarySourceOfIncome = yup.string()
  .required('primarySourceOfIncome.required.error')
  .oneOf(sourceOfIncomeValues, 'primarySourceOfIncome.enum.error');

export const additionalMonthlyGrossIncome = yup.string()
  .notRequired()
  .transform((value, originalValue) => (originalValue === '' ? undefined : value));

export const secondSourceOfIncomeConditionals = {
  required: yup.string()
    .required('secondSourceOfIncome.required.error')
    .oneOf(sourceOfIncomeValues, 'secondSourceOfIncome.enum.error'),
  notRequired: yup.string().notRequired(),
};

export const secondSourceOfIncome = yup.string()
  .when('additionalMonthlyGrossIncome', ([grossIncome]) => {
    if (grossIncome) {
      return secondSourceOfIncomeConditionals.required;
    }

    return secondSourceOfIncomeConditionals.notRequired;
  });

export const suppIndustryConditionals = {
  required: yup.string().required('supplementary.industry.required.error'),
  notRequired: yup.string().notRequired(),
};

export const suppOccupationConditionals = {
  required: yup.string().required('supplementary.occupation.required.error'),
  notRequired: yup.string().notRequired(),
};

export const supplementary = {
  plasticColor: yup.string().required().oneOf(allowedPlasticColors, 'supplementary.plasticColor.oneOf.error'),

  employmentStatus: yup.string()
    .required('supplementary.employmentStatus.required.error')
    .oneOf(employmentStatusValues, 'supplementary.employmentStatus.enum.error'),

  industry: yup.string().when('profession', ([profession]) => {
    if (suppEmploymentStatusValuesRequired.includes(profession)) {
      return suppIndustryConditionals.required;
    }
    return suppIndustryConditionals.notRequired;
  }),

  occupation: yup.string().when('profession', ([profession]) => {
    if (suppEmploymentStatusValuesRequired.includes(profession)) {
      return suppOccupationConditionals.required;
    }
    return suppOccupationConditionals.notRequired;
  }),
};


